import { executeOrbitStrategyAction, loadOrbitStrategyDetail, mergeOrbitDetail } from '@/api/orbit-strategy'
import { mapRobot } from '@/utils/portal-data'
import { loadStrategyCapacity } from '@/api/portal'

// Only called from Orbit branches in UserPortal; other strategy workflows stay untouched.
export default {
  data() {
    return {
      orbitActionVisible: false,
      orbitActionType: 'modify',
      orbitActionRobot: { raw: {} },
      orbitActionSubmitting: false
    }
  },
  methods: {
    orbitErrorText(error, fallbackKey) {
      if (error && error.i18nKey) return this.$tk(error.i18nKey)
      if (error && error.message) return this.$t(error.message)
      return this.$tk(fallbackKey)
    },
    applyOrbitRecord(target, data) {
      if (!target || target.productType !== 'orbit' || !data || typeof data !== 'object') return
      const raw = mergeOrbitDetail(target.raw, data)
      const replacement = { ...mapRobot(raw, 0), id: target.id, rank: target.rank, rankText: target.rankText }
      const matches = robot => robot && robot.productType === 'orbit' && String(robot.id) === String(target.id)
      this.robots = this.robots.map(robot => matches(robot) ? replacement : robot)
      if (matches(this.orbitActionRobot)) this.orbitActionRobot = replacement
      if (matches(this.detailRobotCache)) {
        this.detailRobotCache = replacement
        sessionStorage.setItem('uplusStrategyDetail', JSON.stringify(replacement))
      }
      return replacement
    },
    async openOrbitAction({ action, robot }) {
      if (!robot || robot.productType !== 'orbit' || robot.raw && robot.raw.__historyStrategy) return
      if (this.orbitActionSubmitting) return
      this.orbitActionType = action
      this.orbitActionRobot = robot
      this.orbitActionVisible = true
    },
    async refreshOrbitActionRecord(robot) {
      // Missing identity cannot be replaced with a UID/id from another strategy API.
      if (!robot.raw || !robot.raw.sid) {
        await this.refreshVisibleStrategies()
        const latest = this.robots.find(item => item.productType === 'orbit' && String(item.id) === String(robot.id))
        if (latest) this.orbitActionRobot = latest
        return Boolean(latest && latest.raw && latest.raw.sid && latest.raw.version !== undefined)
      }
      const result = await loadOrbitStrategyDetail(robot, { only: 'detail' })
      if (result.detail) this.applyOrbitRecord(robot, result.detail)
      return Boolean(result.detail)
    },
    async handleOrbitActionSubmit(payload) {
      if (this.orbitActionSubmitting) return
      this.orbitActionSubmitting = true
      try {
        // A refreshed version must be used on a retry; never automatically replay a mutation.
        const robot = this.orbitActionRobot
        const result = await executeOrbitStrategyAction({ ...payload, robot })
        this.applyOrbitRecord(robot, result.data)
        if (payload.action === 'delete') {
          const capacityResult = await loadStrategyCapacity()
          if (capacityResult.capacity) this.strategyCapacity = capacityResult.capacity
        }
        this.orbitActionVisible = false
        if (payload.action === 'delete' && this.isStrategyDetail) {
          this.detailRobotCache = null
          sessionStorage.removeItem('uplusStrategyDetail')
          await this.$router.push('/app/robots')
        }
        // The App refreshes the list after an Orbit action. No overview/wallet endpoints are needed.
        await this.refreshVisibleStrategies()
        this.showToast(result.message || this.$tk('robot.orbit_action_success'), 'success')
      } catch (error) {
        if (['remote_strategy_version_conflict', 'orbit_metadata_missing'].includes(error.code)) {
          const refreshed = await this.refreshOrbitActionRecord(this.orbitActionRobot).catch(() => false)
          this.showToast(refreshed ? this.$tk('robot.orbit_state_refreshed') : this.orbitErrorText(error, 'robot.orbit_data_load_failed'), 'error')
        } else {
          this.showToast(this.orbitErrorText(error, 'robot.orbit_operation_failed'), 'error')
        }
      } finally {
        this.orbitActionSubmitting = false
      }
    }
  }
}
